import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

export interface BreakdownSection {
  title: string
  rows: Array<{ label: string; cost_usd: number; calls?: number }>
}

interface ServiceCardProps {
  name: string
  color: string
  costUsd: number
  details: Record<string, string | number>
  warning?: string
  breakdowns?: BreakdownSection[]
}

export function ServiceCard({
  name,
  color,
  costUsd,
  details,
  warning,
  breakdowns,
}: ServiceCardProps) {
  const [expanded, setExpanded] = useState(false)

  const detailEntries = Object.entries(details)

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.8)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: color,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, textAlign: 'left' }}>
          <span
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--ink)',
            }}
          >
            {name}
          </span>
        </div>
        <span
          style={{
            fontFamily: 'var(--serif)',
            fontSize: '20px',
            fontWeight: 600,
            color: 'var(--ink)',
          }}
        >
          ${costUsd.toFixed(2)}
        </span>
        {expanded ? (
          <ChevronDown size={16} color="var(--ink-muted)" />
        ) : (
          <ChevronRight size={16} color="var(--ink-muted)" />
        )}
      </button>

      {expanded && (
        <div
          style={{
            padding: '0 20px 16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '12px',
          }}
        >
          {detailEntries.map(([key, value]) => (
            <div key={key}>
              <div
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: 'var(--ink-muted)',
                  marginBottom: '2px',
                }}
              >
                {key.replace(/_/g, ' ')}
              </div>
              <div
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--ink)',
                }}
              >
                {typeof value === 'number' ? value.toLocaleString() : value}
              </div>
            </div>
          ))}
          {warning && (
            <div
              style={{
                gridColumn: '1 / -1',
                padding: '8px 12px',
                background: '#fffbeb',
                borderRadius: '6px',
                fontFamily: 'var(--sans)',
                fontSize: '12px',
                color: '#d97706',
              }}
            >
              {warning}
            </div>
          )}
          {breakdowns?.map((section) =>
            section.rows.length === 0 ? null : (
              <div
                key={section.title}
                style={{
                  gridColumn: '1 / -1',
                  borderTop: '1px solid var(--border)',
                  paddingTop: '12px',
                  marginTop: '4px',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--sans)',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'var(--ink-muted)',
                    marginBottom: '8px',
                  }}
                >
                  {section.title}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {section.rows.map((row) => (
                    <div
                      key={row.label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        fontFamily: 'var(--sans)',
                        fontSize: '13px',
                        color: 'var(--ink)',
                        padding: '4px 0',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginRight: '12px',
                        }}
                        title={row.label}
                      >
                        {row.label}
                        {row.calls !== undefined && (
                          <span style={{ color: 'var(--ink-muted)', marginLeft: '6px' }}>
                            · {row.calls} call{row.calls === 1 ? '' : 's'}
                          </span>
                        )}
                      </span>
                      <span style={{ fontWeight: 500, flexShrink: 0 }}>
                        ${row.cost_usd.toFixed(4)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}
